import { Plus } from "lucide-react";
import { useId, useState } from "react";

import type { Patient } from "@elation/api-client/src/stitch-web/models";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Button } from "@src/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@src/shared/components/ui/dialog";

import type { CreatePatientFormValues } from "../../../../domain";
import {
  CreatePatientFormV2,
  useCreatePatientFormSubmission,
} from "../CreatePatientForm";

export interface CreatePatientDialogV2Props {
  canCreatePatient?: boolean;
  defaultOpen?: boolean;
  initialValues?: Partial<CreatePatientFormValues>;
  onCreated?: (patient: Patient | null) => Promise<void> | void;
}

export function CreatePatientDialogV2({
  canCreatePatient = true,
  defaultOpen = false,
  initialValues,
  onCreated,
}: CreatePatientDialogV2Props) {
  const [open, setOpen] = useState(defaultOpen);
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const { error, isSubmitting, reset, submitFormValues } =
    useCreatePatientFormSubmission({ onCreated });

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;
    setOpen(nextOpen);
    if (!nextOpen) reset();
  };

  const handleSubmit = async (values: CreatePatientFormValues) => {
    await submitFormValues(values);
    handleOpenChange(false);
  };

  if (!canCreatePatient) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-8 rounded-[9px] bg-surface text-[12.5px] font-semibold shadow-none"
        onClick={() => handleOpenChange(true)}
      >
        <Plus className="size-4" aria-hidden="true" />
        Create a patient
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className="w-[min(calc(100vw-2rem),880px)] max-w-none rounded-[28px] border-border p-9 shadow-soft"
        >
          <DialogHeader className="space-y-0 text-left">
            <DialogTitle
              id={titleId}
              className="text-[28px] font-semibold leading-none tracking-[-0.02em] text-surface-foreground"
            >
              New patient
            </DialogTitle>
            <DialogDescription id={descriptionId} className="sr-only">
              Enter the patient&apos;s demographics to create their{" "}
              {PRODUCT_NAME.singular} record.
            </DialogDescription>
          </DialogHeader>

          <CreatePatientFormV2
            className="mt-8"
            error={error}
            initialValues={initialValues}
            isSubmitting={isSubmitting}
            onCancel={() => handleOpenChange(false)}
            onSubmit={handleSubmit}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
